/**
 * The map's floor, pre-rendered once to an offscreen canvas (DESIGN 6.2
 * layer 1). With the level's baked plates from the art library (one for the
 * arena, several side by side for a big story map; the arena's:
 * our own floor slabs, pond rim, walls and gate arches, baked in the cast's
 * pixel look in this ground projection) it paints that; until it lands, or
 * for a level without one, it paints dark stone slabs with seeded tile noise,
 * grout lines, the water, pillar bases, wall blocks, the spawn gates and the
 * boundary walls. The north wall gets a front face (2.5D: faces that look
 * south are visible); the other walls show their top surface only. The red
 * glow at each gate goes on top either way.
 *
 * Per frame the ground costs one `drawImage` plus a couple of animated pond
 * ripples; a blizzard overlays frozen water (DESIGN 2.9 Blizzard).
 */

import type { Arena } from '@shared/index.js';
import { ARENA, mulberry32 } from '@shared/index.js';
import { GROUND, rgba } from '../palette.js';
import { TAU, UNIT, WALL_H, type Ctx } from './gfx.js';
import { art } from './sprites.js';

/** Units of wall/void drawn outside the map rectangle (west, east, south). */
export const GROUND_PAD = 1.5;
/** Units above the map's north edge: the north wall's face and the gate arch rise into it. */
export const GROUND_TOP = 3;
/** Width of a gate opening in units. */
const GATE_W = 2.2;
const WALL_T = 1.0;

export class Ground {
  private canvas: HTMLCanvasElement | null = null;
  private res = 0;
  private withPlate = false;
  private arena: Arena = ARENA;
  /** Last device-pixel ratio asked for, so a new map can paint at once. */
  private dpr = 1;

  /** The room plays another map: its floor is painted at once. */
  setArena(arena: Arena): void {
    if (arena === this.arena) return;
    this.arena = arena;
    this.withPlate = false;
    this.canvas = null;
    this.ensure(this.dpr);
  }

  /**
   * (Re)build the offscreen floor at `res` device pixels per CSS pixel
   * (capped at 2). A map bigger than the arena with its plate loaded needs
   * no offscreen copy at all: the plate is drawn straight through the camera
   * (a 2x copy of a story map's plate would cost tens of MB).
   */
  ensure(dpr: number): void {
    this.dpr = dpr;
    const big = isBig(this.arena);
    const res = big ? 1 : Math.min(2, Math.max(1, dpr));
    const plates = this.plates();
    if (big && plates) {
      this.canvas = null;
      this.withPlate = true;
      this.res = res;
      return;
    }
    if (this.canvas && this.res === res && this.withPlate === !!plates) return;
    this.paint(res);
  }

  /** Draw the floor through the camera; `blizzard` > 0 freezes the water. */
  draw(ctx: Ctx, sx0: number, sy0: number, blizzard: number, time: number): void {
    // the plates landed after the first paint: repaint once with them
    if (!this.withPlate && this.plates()) this.ensure(this.dpr);
    const a = this.arena;
    if (this.canvas) {
      const w = (a.w + GROUND_PAD * 2) * UNIT;
      const h = (a.h + GROUND_PAD + GROUND_TOP) * UNIT;
      ctx.drawImage(this.canvas, 0, 0, this.canvas.width, this.canvas.height, sx0 - GROUND_PAD * UNIT, sy0 - GROUND_TOP * UNIT, w, h);
    } else {
      const plates = this.plates();
      if (!plates) return;
      const smooth = ctx.imageSmoothingEnabled;
      ctx.imageSmoothingEnabled = false;
      for (const plate of plates) {
        ctx.drawImage(plate.img, sx0 + plate.origin[0] * UNIT, sy0 + plate.origin[1] * UNIT,
          (plate.img.width / plate.pxPerUnit) * UNIT, (plate.img.height / plate.pxPerUnit) * UNIT);
      }
      ctx.imageSmoothingEnabled = smooth;
    }

    for (const z of a.water) {
      if (z.k === 'rect') {
        if (blizzard > 0) {
          ctx.globalAlpha = Math.min(1, blizzard * 2) * 0.85;
          ctx.fillStyle = GROUND.pondFrozen;
          ctx.fillRect(sx0 + z.x * UNIT, sy0 + z.y * UNIT, z.w * UNIT, z.h * UNIT);
          ctx.globalAlpha = 1;
        }
        continue;
      }
      const cx = sx0 + z.x * UNIT;
      const cy = sy0 + z.y * UNIT;
      if (blizzard > 0) {
        ctx.globalAlpha = Math.min(1, blizzard * 2) * 0.85;
        ctx.fillStyle = GROUND.pondFrozen;
        ctx.beginPath();
        ctx.ellipse(cx, cy, z.rx * UNIT, z.ry * UNIT, 0, 0, TAU);
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx - z.rx * UNIT * 0.6, cy - z.ry * UNIT * 0.3);
        ctx.lineTo(cx - z.rx * UNIT * 0.1, cy + z.ry * UNIT * 0.1);
        ctx.lineTo(cx + z.rx * UNIT * 0.35, cy - z.ry * UNIT * 0.45);
        ctx.moveTo(cx - z.rx * UNIT * 0.1, cy + z.ry * UNIT * 0.1);
        ctx.lineTo(cx + z.rx * UNIT * 0.15, cy + z.ry * UNIT * 0.6);
        ctx.stroke();
        ctx.globalAlpha = 1;
        continue;
      }
      // Two slow ripple rings so the pond reads as water from across the arena.
      ctx.strokeStyle = 'rgba(140,190,255,0.35)';
      ctx.lineWidth = 1;
      for (let i = 0; i < 2; i++) {
        const f = ((time * 0.25 + i * 0.5) % 1);
        ctx.globalAlpha = (1 - f) * 0.6;
        ctx.beginPath();
        ctx.ellipse(cx, cy, z.rx * UNIT * (0.15 + f * 0.8), z.ry * UNIT * (0.15 + f * 0.8), 0, 0, TAU);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
  }

  /** The level's baked floor plates in order, once every one has landed (null until then, or without plates). */
  private plates(): Plate[] | null {
    const names = this.arena.plates;
    if (names.length === 0) return null;
    const out: Plate[] = [];
    for (const name of names) {
      const im = art.image(name);
      if (!im || !im.origin || !im.pxPerUnit) return null;
      out.push({ img: im.img, origin: im.origin, pxPerUnit: im.pxPerUnit });
    }
    return out;
  }

  private paint(res: number): void {
    const a = this.arena;
    const plates = this.plates();
    this.res = res;
    this.withPlate = !!plates;
    const c = this.canvas ?? document.createElement('canvas');
    const px = UNIT * res;
    c.width = Math.ceil((a.w + GROUND_PAD * 2) * px);
    c.height = Math.ceil((a.h + GROUND_PAD + GROUND_TOP) * px);
    const g = c.getContext('2d');
    if (g) {
      g.setTransform(px, 0, 0, px, GROUND_PAD * px, GROUND_TOP * px); // world units, origin at the map's (0,0)
      if (plates) {
        // pixel art scaled up by whole steps stays crisp
        g.imageSmoothingEnabled = false;
        g.fillStyle = GROUND.void;
        g.fillRect(-GROUND_PAD, -GROUND_TOP, a.w + GROUND_PAD * 2, a.h + GROUND_PAD + GROUND_TOP);
        for (const plate of plates) g.drawImage(plate.img, plate.origin[0], plate.origin[1], plate.img.width / plate.pxPerUnit, plate.img.height / plate.pxPerUnit);
      } else {
        paintFloor(g, a);
        paintWater(g, a);
        paintDeep(g, a);
        paintChasm(g, a);
        paintPillarBases(g, a);
        paintBlocks(g, a);
        paintWalls(g, a);
        paintGates(g, a);
      }
      paintGateGlows(g, a);
      if (a.id === ARENA.id) paintTrailGlyph(g);
    }
    this.canvas = c;
  }
}

interface Plate { img: CanvasImageSource & { width: number; height: number }; origin: readonly [number, number] | number[]; pxPerUnit: number }

/** Bigger than the wave arena: drawn from its plates directly once they land. */
function isBig(a: Arena): boolean {
  return a.w * a.h > ARENA.w * ARENA.h;
}

/** Deep water (story maps), until the plate lands: dark water with a lighter rim. */
function paintDeep(g: Ctx, a: Arena): void {
  if (a.deepCells.length === 0) return;
  g.fillStyle = GROUND.pondDeep;
  for (const cell of a.deepCells) g.fillRect(cell % a.w, Math.floor(cell / a.w), 1, 1);
  g.strokeStyle = GROUND.pondRim;
  g.lineWidth = 0.08;
  for (const b of a.deep) g.strokeRect(b.x, b.y, b.w, b.h);
}

/** The chasm (story maps), until the plate lands: a black pit, its north lip lit like a cliff edge. */
function paintChasm(g: Ctx, a: Arena): void {
  if (a.pitCells.length === 0) return;
  const pits = new Set(a.pitCells);
  for (const cell of a.pitCells) {
    const x = cell % a.w, y = Math.floor(cell / a.w);
    g.fillStyle = GROUND.void;
    g.fillRect(x, y, 1, 1);
    if (!pits.has(cell - a.w)) {
      // the far wall of the drop, seen from the south
      g.fillStyle = GROUND.wallFace;
      g.fillRect(x, y, 1, 0.45);
    }
  }
}

// ------------------------------------------------------------------ painters (world units, run once)

function paintFloor(g: Ctx, a: Arena): void {
  const rnd = mulberry32(0x5eed);
  g.fillStyle = GROUND.void;
  g.fillRect(-GROUND_PAD, -GROUND_TOP, a.w + GROUND_PAD * 2, a.h + GROUND_PAD + GROUND_TOP);
  g.fillStyle = GROUND.floor;
  g.fillRect(0, 0, a.w, a.h);

  // 2 x 1 u slabs in a running bond; each slab's lightness varies a little.
  for (let y = 0; y < a.h; y++) {
    const offset = y % 2 === 0 ? 0 : 1;
    for (let x = -1; x < a.w; x += 2) {
      const sx = x + offset;
      const n = rnd();
      const shade = 0.08 + n * 0.1;
      g.fillStyle = n > 0.5 ? rgba(GROUND.floorAlt, shade) : rgba('#000000', shade * 0.5);
      g.fillRect(Math.max(0, sx), y, Math.min(2, a.w - Math.max(0, sx)), 1);
      if (n > 0.92) {
        // occasional crack
        g.strokeStyle = rgba('#000000', 0.3);
        g.lineWidth = 0.04;
        g.beginPath();
        const cx = Math.max(0.2, sx + 0.4 + rnd() * 1.2);
        const cy = y + 0.2 + rnd() * 0.6;
        g.moveTo(cx, cy);
        g.lineTo(cx + 0.3 + rnd() * 0.4, cy + (rnd() - 0.5) * 0.4);
        g.lineTo(cx + 0.5 + rnd() * 0.5, cy + (rnd() - 0.5) * 0.5);
        g.stroke();
      }
    }
  }
  // grout
  g.strokeStyle = GROUND.grout;
  g.lineWidth = 0.05;
  g.beginPath();
  for (let y = 0; y <= a.h; y++) {
    g.moveTo(0, y);
    g.lineTo(a.w, y);
  }
  for (let y = 0; y < a.h; y++) {
    const offset = y % 2 === 0 ? 0 : 1;
    for (let x = offset; x <= a.w; x += 2) {
      g.moveTo(x, y);
      g.lineTo(x, y + 1);
    }
  }
  g.stroke();
  // Subtle vignette toward the walls so the map reads as a lit stage.
  const vg = g.createRadialGradient(a.w / 2, a.h / 2, a.h * 0.35, a.w / 2, a.h / 2, a.w * 0.75);
  vg.addColorStop(0, 'rgba(0,0,0,0)');
  vg.addColorStop(1, 'rgba(0,0,0,0.35)');
  g.fillStyle = vg;
  g.fillRect(0, 0, a.w, a.h);
}

function paintWater(g: Ctx, a: Arena): void {
  for (const z of a.water) {
    if (z.k === 'rect') {
      g.fillStyle = GROUND.pond;
      g.fillRect(z.x, z.y, z.w, z.h);
      g.strokeStyle = GROUND.pondRim;
      g.lineWidth = 0.08;
      g.strokeRect(z.x, z.y, z.w, z.h);
      continue;
    }
    g.save();
    g.translate(z.x, z.y);
    // soft bank
    g.fillStyle = rgba('#000000', 0.35);
    g.beginPath();
    g.ellipse(0, 0.08, z.rx + 0.25, z.ry + 0.2, 0, 0, TAU);
    g.fill();
    const grad = g.createRadialGradient(0, 0, 0.2, 0, 0, z.rx);
    grad.addColorStop(0, GROUND.pondDeep);
    grad.addColorStop(1, GROUND.pond);
    g.fillStyle = grad;
    g.beginPath();
    g.ellipse(0, 0, z.rx, z.ry, 0, 0, TAU);
    g.fill();
    g.strokeStyle = GROUND.pondRim;
    g.lineWidth = 0.08;
    g.stroke();
    // highlights
    g.strokeStyle = 'rgba(140,190,255,0.25)';
    g.lineWidth = 0.05;
    g.beginPath();
    g.ellipse(-0.6, -0.5, z.rx * 0.45, z.ry * 0.35, 0, 0, TAU);
    g.stroke();
    g.restore();
  }
}

function paintPillarBases(g: Ctx, a: Arena): void {
  for (const p of a.pillars) {
    g.fillStyle = GROUND.pillarBase;
    g.beginPath();
    g.ellipse(p.x, p.y + 0.05, p.r + 0.3, (p.r + 0.3) * 0.5, 0, 0, TAU);
    g.fill();
  }
}

/** Interior wall blocks of a multi-room level: a raised top and, facing the camera, a front face. */
/** The permanent walls (doors and brambles change, so they draw themselves on top). */
function paintBlocks(g: Ctx, a: Arena): void {
  for (const b of a.walls) {
    g.fillStyle = GROUND.wallFace;
    g.fillRect(b.x, b.y + b.h - WALL_H, b.w, WALL_H);
    g.fillStyle = GROUND.wallTop;
    g.fillRect(b.x, b.y - WALL_H, b.w, b.h);
    g.fillStyle = GROUND.wallEdge;
    g.fillRect(b.x, b.y + b.h - WALL_H, b.w, 0.06);
  }
}

function paintWalls(g: Ctx, a: Arena): void {
  const w = a.w, h = a.h;
  // North wall: top surface then front face (facing the camera).
  g.fillStyle = GROUND.wallTop;
  g.fillRect(-WALL_T, -WALL_T - WALL_H, w + WALL_T * 2, WALL_T);
  g.fillStyle = GROUND.wallFace;
  g.fillRect(-WALL_T, -WALL_H, w + WALL_T * 2, WALL_H);
  g.fillStyle = GROUND.wallEdge;
  g.fillRect(-WALL_T, -WALL_H, w + WALL_T * 2, 0.06);
  // Brick lines on the face
  g.strokeStyle = rgba('#000000', 0.35);
  g.lineWidth = 0.03;
  g.beginPath();
  for (let x = -WALL_T; x <= w + WALL_T; x += 1.5) {
    g.moveTo(x, -WALL_H + 0.06);
    g.lineTo(x, 0);
  }
  g.moveTo(-WALL_T, -WALL_H / 2);
  g.lineTo(w + WALL_T, -WALL_H / 2);
  g.stroke();
  // West / east walls: top surfaces (their faces look away from the camera).
  g.fillStyle = GROUND.wallTop;
  g.fillRect(-WALL_T, -WALL_T - WALL_H, WALL_T, h + WALL_T * 2 + WALL_H);
  g.fillRect(w, -WALL_T - WALL_H, WALL_T, h + WALL_T * 2 + WALL_H);
  // South wall: top surface, raised by WALL_H like every top.
  g.fillRect(-WALL_T, h - WALL_H, w + WALL_T * 2, WALL_T);
  g.fillStyle = GROUND.wallFace;
  g.fillRect(-WALL_T, h - WALL_H + WALL_T, w + WALL_T * 2, WALL_H * 0.5);
  // Inner edge highlight all round
  g.strokeStyle = GROUND.wallEdge;
  g.lineWidth = 0.05;
  g.strokeRect(-0.025, -0.025, w + 0.05, h + 0.05);
}

function paintGates(g: Ctx, a: Arena): void {
  for (const gate of a.gates) {
    // only gates in the boundary wall get an arch; a story map's gates out on the floor are cracks in the plate
    if (gate.x >= 1 && gate.y >= 1 && gate.x <= a.w - 1 && gate.y <= a.h - 1) continue;
    g.save();
    g.translate(gate.x, gate.y);
    const north = gate.y < 1, south = gate.y > a.h - 1, west = gate.x < 1;
    if (north || south) {
      const y0 = north ? -WALL_T - WALL_H - 0.6 : WALL_T - WALL_H - 0.5;
      g.fillStyle = GROUND.gate;
      g.fillRect(-GATE_W / 2, y0, GATE_W, WALL_T + WALL_H + 0.6);
      g.strokeStyle = GROUND.gateFrame;
      g.lineWidth = 0.1;
      g.strokeRect(-GATE_W / 2, y0, GATE_W, WALL_T + WALL_H + 0.6);
    } else {
      const x0 = west ? -WALL_T - 0.6 : -0.5;
      g.fillStyle = GROUND.gate;
      g.fillRect(x0, -GATE_W / 2 - WALL_H, WALL_T + 0.6, GATE_W);
      g.strokeStyle = GROUND.gateFrame;
      g.lineWidth = 0.1;
      g.strokeRect(x0, -GATE_W / 2 - WALL_H, WALL_T + 0.6, GATE_W);
    }
    g.restore();
  }
}

/**
 * A glyph cut into the top of the arena's east wall, level with the north-east pillar (the camera
 * keeps that wall in view; the north wall sits under the top bar): the first clue of the glyph
 * trail across his sites, with one dot for its place. An Eventide glyph is a horizon with three
 * stations, each empty (0), a ray up (1) or a ray down to a drop (2). Art only; hovering it
 * shows the trail's hint (trailGlyphHint, main.ts).
 */
const TRAIL_GLYPH = { code: '021', order: 1, x: 40.05, y: 8.55, size: 0.9 };
const TRAIL_HINT = 'The first of five marks. Read them in order, then follow the sun down.';

/** The trail's hint when a world point is over the wall glyph (a small margin around it), else null. */
export function trailGlyphHint(arena: Arena, x: number, y: number): string | null {
  if (arena.id !== ARENA.id) return null;
  const { x: gx, y: gy, size } = TRAIL_GLYPH;
  const pad = 0.15;
  return x >= gx - pad && x <= gx + size + pad && y >= gy - pad && y <= gy + size + pad ? TRAIL_HINT : null;
}

function paintTrailGlyph(g: Ctx): void {
  const { code, order, x, y, size: s } = TRAIL_GLYPH;
  const stations = [0.22, 0.5, 0.78];
  const cut = (dy: number, color: string): void => {
    const X = (u: number): number => x + u * s;
    const Y = (v: number): number => y + dy + v * s;
    g.strokeStyle = color;
    g.fillStyle = color;
    g.lineWidth = 0.075 * s;
    g.lineCap = 'round';
    g.beginPath();
    g.moveTo(X(0.06), Y(0.55));
    g.lineTo(X(0.94), Y(0.55));
    stations.forEach((sx, i) => {
      if (code[i] === '0') return;
      g.moveTo(X(sx), Y(0.55));
      g.lineTo(X(sx), Y(code[i] === '1' ? 0.22 : 0.76));
    });
    g.stroke();
    g.beginPath();
    const dot = (u: number, v: number, r: number): void => {
      g.moveTo(X(u) + r * s, Y(v));
      g.arc(X(u), Y(v), r * s, 0, TAU);
    };
    stations.forEach((sx, i) => { if (code[i] === '2') dot(sx, 0.89, 0.055); });
    for (let k = 0; k < order; k++) dot(0.5 + (k - (order - 1) / 2) * 0.13, 0.07, 0.035);
    g.fill();
  };
  g.save();
  cut(0.035, 'rgba(200,210,235,0.22)'); // the cut's lit lower lip
  cut(0, 'rgba(12,14,24,0.6)'); // the cut itself
  g.restore();
}

/** A red threshold glow on the floor at each gate, so spawn points are readable. */
function paintGateGlows(g: Ctx, a: Arena): void {
  for (const gate of a.gates) {
    g.save();
    g.translate(gate.x, gate.y);
    const grad = g.createRadialGradient(0, 0, 0, 0, 0, 1.6);
    grad.addColorStop(0, 'rgba(255,80,80,0.18)');
    grad.addColorStop(1, 'rgba(255,80,80,0)');
    g.fillStyle = grad;
    g.beginPath();
    g.arc(0, 0, 1.6, 0, TAU);
    g.fill();
    g.restore();
  }
}
